import React, { useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@components/buttons";
import { MostPlayTicketActionsTypeEnum } from "@data-types/analytics-tracker/QuickPlayInteractionTypeEnum";
import { useRootAppStore } from "@hooks";
import { useFixBody } from "@state/hooks/shared";
import { ChangePageButton } from "@state/quick-play-store/MostPlayedTicketsStore";
import { getCurrentCulture } from "@utils";
import { analyticsTracker } from "@utils/analytics";
import classNames from "classnames";
import { observer } from "mobx-react";

import { QuickPlayPopupsPagination } from "../common/QuickPlayPopupsPagination";
import { MostPlayedTicketsPopupBody } from "./components/MostPlayedTicketsPopupBody";

export default observer(function MostPlayTicketsPopup() {
	const { t } = useTranslation("mybets");

	const rootStore = useRootAppStore();

	const popupElement = useRef<null | HTMLDivElement>(null);
	const location = useLocation();

	useFixBody(
		true,
		rootStore.quickPlayStore.mostPlayedTicketsStore.isPopupDisplayed
	);

	if (
		!location.pathname.includes("home") &&
		!location.pathname.includes("most-played-tickets")
	) {
		rootStore.quickPlayStore.mostPlayedTicketsStore.deactivatePopup();
	}

	useEffect(() => {
		analyticsTracker.trackModalInteraction("most-played");
	}, []);

	useEffect(() => {
		popupElement.current?.scrollTo({ top: 0 });
	}, [rootStore.quickPlayStore.mostPlayedTicketsStore.selectedBetSlip]);

	const isOnQuickPlayPage = location.pathname.includes("quick-play");

	const isPrevAndNextDisabled =
		rootStore.quickPlayStore.mostPlayedTicketsStore.currentActiveList
			.size == 1;

	if (!rootStore.quickPlayStore.mostPlayedTicketsStore.selectedBetSlip) {
		return null;
	}
	return (
		<div className="popup popup--play popup--mpt">
			<div className="popup__card popup--mpt__card">
				<div className="popup__header">
					<div className="popup__title">
						<i className="u-icon u-icon--base u-icon--top--str u-color--rate--low u-mar--right--xtny" />

						<div className="popup__title__inner">
							{t("QUICK_PLAY.MOST_PLAYED_TICKETS.TICKET")} #
							{
								rootStore.quickPlayStore.mostPlayedTicketsStore
									.betSlipIndex
							}
						</div>
					</div>

					<button
						type="button"
						onClick={
							rootStore.quickPlayStore.mostPlayedTicketsStore
								.deactivatePopup
						}
						className="popup__close btn btn--square btn--square--sml btn--primary--inverted"
					>
						<i className="u-icon u-icon--tny u-icon--close u-color--text--secondary" />
					</button>
				</div>

				<div
					ref={popupElement}
					className="popup__body popup--qp__body popup--bbp__body"
				>
					<MostPlayedTicketsPopupBody
						key={
							rootStore.quickPlayStore.mostPlayedTicketsStore
								.selectedBetSlip.betSlipId
						}
						betSlip={
							rootStore.quickPlayStore.mostPlayedTicketsStore
								.selectedBetSlip
						}
					/>
				</div>

				<div className="popup__footer popup__footer--bordered">
					<Button
						type="button"
						disabled={
							rootStore.quickPlayStore.loaderStore
								.isLoadingProcess
						}
						onClick={() => {
							if (
								!rootStore.quickPlayStore.mostPlayedTicketsStore
									.selectedBetSlip
							) {
								return;
							}
							analyticsTracker.trackQuickPlayInteraction(
								MostPlayTicketActionsTypeEnum.TicketUse
							);
							if (
								!rootStore.quickPlayStore.mostPlayedTicketsStore.isInBetSlip.has(
									rootStore.quickPlayStore
										.mostPlayedTicketsStore.selectedBetSlip
										.betSlipId
								)
							) {
								analyticsTracker.trackUseTicketEvent(
									"most-played"
								);
							}
							rootStore.quickPlayStore.mostPlayedTicketsStore.addOrRemoveMostPlayedTicket(
								rootStore.quickPlayStore.mostPlayedTicketsStore
									.selectedBetSlip
							);
							rootStore.quickPlayStore.mostPlayedTicketsStore.deactivatePopup();
						}}
						btnText={rootStore.quickPlayStore.mostPlayedTicketsStore.selectedBetSlip.maxCoefficient.toFixed(
							2
						)}
						className={classNames(
							"btn--med btn--block quote--button popup--mpt__quota",
							{
								"is-active":
									rootStore.quickPlayStore.mostPlayedTicketsStore.isInBetSlip.has(
										rootStore.quickPlayStore
											.mostPlayedTicketsStore
											.selectedBetSlip.betSlipId
									),
								"btn--icon inc":
									rootStore.quickPlayStore
										.mostPlayedTicketsStore.selectedBetSlip
										.quotaIndicator > 0,
								"btn--icon desc":
									rootStore.quickPlayStore
										.mostPlayedTicketsStore.selectedBetSlip
										.quotaIndicator < 0,
							}
						)}
					/>
					<div className="popup__footer__row u-display--f--center">
						<QuickPlayPopupsPagination
							onPrevClick={() => {
								rootStore.quickPlayStore.mostPlayedTicketsStore.changePage(
									ChangePageButton.PREVIOUS
								);
								analyticsTracker.trackModalSwitcher(
									"most-played"
								);
							}}
							onNextClick={() => {
								rootStore.quickPlayStore.mostPlayedTicketsStore.changePage(
									ChangePageButton.NEXT
								);
								analyticsTracker.trackModalSwitcher(
									"most-played"
								);
							}}
							isDisabled={
								rootStore.quickPlayStore.loaderStore
									.isLoadingProcess || isPrevAndNextDisabled
							}
							currentPage={
								rootStore.quickPlayStore.mostPlayedTicketsStore
									.betSlipIndex
							}
							totalPages={
								rootStore.quickPlayStore.mostPlayedTicketsStore
									.currentActiveList.size
							}
						/>
					</div>
					{!isOnQuickPlayPage && (
						<Link
							to={`/${getCurrentCulture()}/quick-play/most-played-tickets`}
							className="btn btn--sml btn--block btn--ghost popup__btn popup__btn--tertiary"
							type="button"
						>
							<div className="btn__label">
								{t("COMMON.SHOW_MORE")}
							</div>
						</Link>
					)}
				</div>
			</div>
		</div>
	);
});
